import { useState, useRef, useEffect } from 'react';
import type { ChatMessage, SourceRef, SourceKind } from '../../types';
import { ScreenFrame } from '../layout/ScreenFrame';
import { PageHeader } from '../layout/PageHeader';
import { SourceIcon, NavIcon } from '../primitives';
import { GooeyThinkingDots } from '../theme90s/GooeyThinkingDots';
import { use90s } from '../../hooks/use90s';
import { useAsk } from '../../hooks/useApi';
import { relTime } from '../../utils/time';

const USER_ID = 'adam';

const SUGGESTED_PROMPTS = [
  "What did we decide about on-chain settlement?",
  "Summarize the Acme Corp discovery call",
  "What's blocking FRM-412?",
  "What does our handbook say about cap table imports?",
];

function MessageBubble({ msg }: { msg: ChatMessage }) {
  if (msg.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          className="msg-user"
          style={{
            maxWidth: '78%',
            padding: '10px 14px',
            background: 'var(--deep)',
            color: 'white',
            borderRadius: '14px 14px 4px 14px',
            fontSize: 13.5,
            lineHeight: 1.5,
          }}
        >
          {msg.text}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ paddingTop: 2 }}>
        <SourceIcon kind="ai" size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="msg-assistant"
          style={{
            fontSize: 13.5,
            lineHeight: 1.6,
            color: 'var(--deep)',
            background: 'var(--surface)',
            border: '1px solid var(--line-soft)',
            padding: '12px 14px',
            borderRadius: '4px 14px 14px 14px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {msg.text}
        </div>
        {msg.sources && msg.sources.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--slate)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Sources
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {msg.sources.map((s, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    background: 'var(--white)',
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    cursor: 'pointer',
                  }}
                >
                  <SourceIcon kind={s.src} size={14} />
                  <span
                    style={{
                      fontSize: 12.5,
                      color: 'var(--deep)',
                      fontWeight: 600,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.title}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--slate-light)', fontFamily: 'var(--font-mono)' }}>
                    {s.t}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AskScreen() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const is90s = use90s();
  const askMutation = useAsk();

  const send = (text?: string) => {
    const q = (text ?? input).trim();
    if (!q || askMutation.isPending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);

    askMutation.mutate(
      { query: q, userId: USER_ID, limit: 8 },
      {
        onSuccess: (data) => {
          const top = data.results[0];
          const answerText = top
            ? top.chunk_content ?? top.content ?? 'Found results — see sources below.'
            : "I searched across everything but didn't find a clear match. Try rephrasing your question.";

          const sources: SourceRef[] = data.results.slice(0, 5).map((r) => ({
            src: r.source as SourceKind,
            title:
              (r.metadata.title as string) ||
              (r.metadata.subject as string) ||
              (r.metadata.name as string) ||
              r.source,
            t: relTime(r.occurred_at),
          }));

          setMessages((m) => [...m, { role: 'assistant', text: answerText, sources }]);
        },
        onError: (err) => {
          setMessages((m) => [
            ...m,
            {
              role: 'assistant',
              text: `Something went wrong: ${err.message}. Please try again.`,
              sources: [],
            },
          ]);
        },
      },
    );
  };

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, askMutation.isPending]);

  return (
    <ScreenFrame maxWidth={920}>
      <PageHeader
        kicker="MVP · Step 3"
        title="Ask Brain"
        meta={<span>Ask questions across everything we've ingested. Answers cite their sources.</span>}
      />

      {messages.length === 0 ? (
        <div
          className="card-surface"
          style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: 16,
            padding: '36px 28px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              margin: '0 auto 14px',
              background: 'var(--gradient-brand)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 24px -8px rgba(45,0,247,.4)',
            }}
          >
            <SourceIcon kind="ai" size={26} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>
            What do you want to know?
          </div>
          <div style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 22 }}>
            Try one of these, or type your own question.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520, margin: '0 auto' }}>
            {SUGGESTED_PROMPTS.map((p, i) => (
              <button
                key={i}
                onClick={() => send(p)}
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 12,
                  fontFamily: 'inherit',
                  fontSize: 13.5,
                  color: 'var(--deep)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div
          ref={listRef}
          className="card-surface"
          style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: 16,
            padding: 20,
            maxHeight: 520,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {messages.map((m, i) => (
            <MessageBubble key={i} msg={m} />
          ))}
          {askMutation.isPending && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--slate)', fontSize: 13 }}>
              <SourceIcon kind="ai" size={18} />
              <span>Searching across ingested data…</span>
              {is90s ? (
                <GooeyThinkingDots />
              ) : (
                <span style={{ display: 'inline-flex', gap: 3 }}>
                  {[0, 0.2, 0.4].map((delay, i) => (
                    <i
                      key={i}
                      style={{
                        display: 'inline-block',
                        width: 5,
                        height: 5,
                        borderRadius: 3,
                        background: 'var(--blue)',
                        animation: `tdot 1.2s infinite ${delay}s`,
                      }}
                    />
                  ))}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <div style={{ marginTop: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: 16,
            padding: '6px 6px 6px 16px',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask anything about Slack, Zoom, Linear, or the KB…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 14,
              height: 42,
              color: 'var(--deep)',
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || askMutation.isPending}
            style={{
              height: 36,
              padding: '0 16px',
              border: 'none',
              borderRadius: 10,
              background: input.trim() && !askMutation.isPending ? 'var(--deep)' : 'var(--line)',
              color: 'white',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: input.trim() && !askMutation.isPending ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Send
            <NavIcon name="arrow" size={14} />
          </button>
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 11,
            color: 'var(--slate)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          {(['slack', 'zoom', 'linear', 'kb'] as const).map((src) => (
            <span key={src} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <SourceIcon kind={src} size={11} />
              {src === 'kb' ? 'Notion KB' : src.charAt(0).toUpperCase() + src.slice(1)}
            </span>
          ))}
          <span style={{ marginLeft: 'auto' }}>
            Answers based only on data you have access to.
          </span>
        </div>
      </div>
    </ScreenFrame>
  );
}
